import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

export interface PortfolioAccountRow {
  customer_id: string;
  customer_name: string;
  industry: string | null;
  tier: string | null;
  role_type: string;
  region: string | null;
  location: string | null;
  al_level: number | null;
  al_label: string | null;
  al_published_days: number | null;
  rl_aggregate: number | null;
  rl_band: string | null;
  my_rl: number | null;
  team_rl: number | null;
  mapped_pairs: number;
  assessed_pairs: number;
  pending_pairs: number;
  total_possible_pairs: number;
  revenue: number | null;
  renewal_date: string | null;
  renewal_days: number | null;
  account_health: number | null;
  cluster: string | null;
  business_health: number | null;
  business_health_label: string | null;
  needs_attention: boolean;
  attention_reasons: string[];
  primary_sponsors: string | null;
  primary_sponsors_id: string | null;
  secondary_sponsors: string | null;
  secondary_sponsors_id: string | null;
  executive_sponsors?: string[];
  self_assessment_status: string | null;
  last_assessment: string | null;
}

export interface RelationshipPairRow {
  pair_id: string;
  account_id: string;
  account_name: string;
  account_tier: string | null;
  account_cluster: string | null;
  account_region: string | null;
  account_renewal_date: string | null;
  account_renewal_days: number | null;
  contact_id: string;
  contact_name: string;
  contact_title: string | null;
  sponsor_id: string | null;
  sponsor_name: string | null;
  rl_current: number | null;
  rl_goal: number | null;
  rl_gap: number | null;
  assessed_at: string | null;
  assessed_days_ago: number | null;
  mapped_at: string | null;
  mapped_days_ago: number | null;
  is_mine: boolean;
  attention_bucket: string;
  low_score_triggers: string[];
}

export interface LevelMixRow {
  label: string;
  values: Record<string, number>;
  counts?: Record<string, number>;
  total: number;
  unassessed_count?: number | null;
}

export interface PortfolioSummary {
  total_accounts: number;
  primary_count: number;
  secondary_count: number;
  executive_count: number;
  total_revenue: number;
  needs_attention_count: number;
  renewals_90d_count: number;
  unmapped_accounts: number;
  unassessed_pairs: number;
  account_capacity_used: number;
  account_capacity_limit: number | null;
  relationship_pairs_total: number;
  relationship_pairs_limit: number | null;
}

export interface MyPortfolioOverviewData {
  summary: PortfolioSummary;
  level_mix: {
    al: LevelMixRow[];
    rl: LevelMixRow[];
  };
  accounts: PortfolioAccountRow[];
  relationship_pairs: RelationshipPairRow[];
  low_score_criteria: {
    renewal_days: number;
    renewal_enabled: boolean;
    rl_gap_levels: number;
    rl_gap_enabled: boolean;
    assessment_stale_days: number;
    assessment_stale_enabled: boolean;
    signal_recency_days: number;
    signal_recency_enabled: boolean;
  };
  needs_attention_criteria: {
    health_threshold: number;
    renewal_days: number;
    include_triage: boolean;
    include_defend: boolean;
    include_bh_concern: boolean;
    include_unscored: boolean;
    include_unmapped: boolean;
    include_unknown_al: boolean;
  };
}

export type LowScoreCriteria = MyPortfolioOverviewData["low_score_criteria"];
export type NeedsAttentionCriteria =
  MyPortfolioOverviewData["needs_attention_criteria"];

export { DEFAULT_LOW_SCORE_CRITERIA } from "../../Utils/LowScoreCriteria";

export const DEFAULT_NEEDS_ATTENTION_CRITERIA: NeedsAttentionCriteria = {
  health_threshold: 65,
  renewal_days: 90,
  include_triage: false,
  include_defend: false,
  include_bh_concern: true,
  include_unscored: false,
  include_unmapped: false,
  include_unknown_al: false,
};

interface OverviewState {
  data: MyPortfolioOverviewData | null;
  loading: boolean;
  error: string | null;
}

const initialState: OverviewState = {
  data: null,
  loading: false,
  error: null,
};

export const fetchMyPortfolioOverview = createAsyncThunk(
  "myPortfolioOverview/fetch",
  async (
    criteria: Partial<LowScoreCriteria> | undefined = undefined,
    { rejectWithValue }
  ) => {
    try {
      const params = criteria
        ? {
            renewal_days: String(criteria.renewal_days),
            renewal_enabled: String(criteria.renewal_enabled),
            rl_gap_levels: String(criteria.rl_gap_levels),
            rl_gap_enabled: String(criteria.rl_gap_enabled),
            assessment_stale_days: String(criteria.assessment_stale_days),
            assessment_stale_enabled: String(criteria.assessment_stale_enabled),
            signal_recency_days: String(criteria.signal_recency_days),
            signal_recency_enabled: String(criteria.signal_recency_enabled),
          }
        : {};
      const response = await get(API_URLS.getMyPortfolioOverview(params));
      return (response as any)?.content || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch portfolio overview"
      );
    }
  }
);

const myPortfolioOverviewSlice = createSlice({
  name: "myPortfolioOverview",
  initialState,
  reducers: {
    clearPortfolioOverview: state => {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMyPortfolioOverview.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyPortfolioOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchMyPortfolioOverview.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || action.error.message || "Unknown error";
      });
  },
});

export const { clearPortfolioOverview } = myPortfolioOverviewSlice.actions;
export default myPortfolioOverviewSlice.reducer;
